"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Menu, X, LogIn, User } from "lucide-react";
import type { SessionUser } from "@/lib/auth-shared";
import LanguageSwitcher from "./LanguageSwitcher";
import { isFlagOn, type FlagsMap } from "@/lib/feature-flags";
import { getConfigString } from "@/lib/remote-config";
import { useConfigs } from "./LandingConfigsProvider";

interface NavbarProps {
  user: SessionUser | null;
  /**
   * Evaluated feature flags for the LANDING platform. Used to hide
   * nav links whose target section is turned off — prevents users
   * from clicking into a #anchor that doesn't exist on the page.
   */
  flags: FlagsMap;
}

/**
 * Builds the avatar's initials: first letter of the first name plus
 * first letter of the last name (max 2 chars). Falls back to the first
 * character of the email when the user has no display name.
 */
function getInitials(user: SessionUser): string {
  const name = user.name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  }
  return user.email.charAt(0).toUpperCase();
}

/**
 * Top navigation bar — client component.
 *
 * Sticky header with brand logo, anchor-section links (gated by feature
 * flags), language switcher and user avatar / sign-in CTA. Adds a
 * shadow on scroll for visual depth. The "dashboard" CTA routes to
 * `/select-destination` when authenticated, or `/login` otherwise — the
 * old `/dashboard` of the landing was removed and the admin panel now
 * lives at `panel.urbaneggs.com.mx`.
 */
export default function Navbar({ user, flags }: NavbarProps) {
  const t = useTranslations("navbar");
  const configs = useConfigs();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const dashboardHref = user ? "/select-destination" : "/login";

  // Hash anchors point at home section IDs — those are NOT translated
  // because they are DOM identifiers. Each link declares the flag that
  // governs its target section, so when the section is OFF the link is
  // pulled from the navbar (avoids dead anchors).
  const links = [
    {
      label: getConfigString(configs, "navbar_link_why", t("whyUrbanEggs")),
      href: "/#porque",
      flag: "landing_section_why_us",
    },
    {
      label: getConfigString(configs, "navbar_link_eggtypes", t("eggTypes")),
      href: "/#catalogo",
      flag: "landing_section_catalog",
    },
    {
      label: getConfigString(
        configs,
        "navbar_link_verification",
        t("verification"),
      ),
      href: "/#verificacion",
      flag: "landing_section_verification",
    },
    {
      label: getConfigString(
        configs,
        "navbar_link_traceability",
        t("traceability"),
      ),
      // Traceability section is always-on (not flag gated) — same as
      // Countdown — so we don't filter it out via the flag predicate.
      href: "/#trazabilidad",
      flag: null,
    },
    {
      label: getConfigString(configs, "navbar_link_loyalty", "Lealtad"),
      href: "/#lealtad",
      flag: "landing_section_loyalty",
    },
    {
      label: getConfigString(configs, "navbar_link_distributors", "Vende con nosotros"),
      href: "/#distribuidores",
      flag: "landing_section_distributors",
    },
  ].filter((l) => l.flag === null || isFlagOn(flags, l.flag));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Routes con hero oscuro (bg-dark inmediato debajo del navbar) que
  // necesitan que el Navbar arranque OPACO desde scrollY=0. Sin esto,
  // el logo SVG (que tiene partes oscuras) se traslapa sobre el navy
  // y partes del logo desaparecen visualmente. El path puede venir
  // con prefijo de locale ('/es/faq', '/en/faq') — usamos endsWith para
  // matchear ambos.
  const pathname = usePathname();
  const isDarkHeroRoute = pathname?.endsWith("/faq") ?? false;
  const shouldBeOpaque = scrolled || isDarkHeroRoute;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldBeOpaque ? "bg-white shadow-md" : "bg-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center" aria-label="Urban Eggs">
          {/* Logotipo oficial (huevo + texto). El SVG vive en
              public/logo.svg. height=40 cabe en el navbar de h-16,
              width auto preserva el aspect ratio (~3.24:1 → ~130px). */}
          <Image
            src="/logo.svg"
            alt="Urban Eggs"
            width={130}
            height={40}
            priority
            className="h-10 w-auto"
          />
        </Link>

        <ul className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.href}>
              <Link
                href={l.href}
                className="text-sm font-medium text-dark hover:text-secondary transition-colors"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="light" />
          {user ? (
            <Link
              href={dashboardHref}
              title={user.name ?? user.email}
              aria-label={user.name ?? t("myAccount")}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary text-sm font-bold hover:bg-primary hover:text-white transition-colors"
            >
              {getInitials(user)}
            </Link>
          ) : (
            <Link
              href="/login"
              title={t("signIn")}
              aria-label={t("signIn")}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
            >
              <User size={18} />
            </Link>
          )}

        </div>

        <button
          className="md:hidden p-2 text-dark"
          onClick={() => setOpen(!open)}
          aria-label={t("menu")}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-white border-t border-muted/30 px-4 pb-4">
          <ul className="flex flex-col gap-4 pt-4">
            {links.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-dark hover:text-secondary"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li className="pt-2 border-t border-muted/30 flex justify-between items-center">
              {user ? (
                <Link
                  href={dashboardHref}
                  onClick={() => setOpen(false)}
                  title={user.name ?? user.email}
                  className="inline-flex items-center gap-2 text-base font-semibold text-primary"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary text-sm font-bold">
                    {getInitials(user)}
                  </span>
                  <span className="truncate max-w-[160px]">
                    {user.name ?? t("myAccount")}
                  </span>
                </Link>
              ) : (
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 text-base font-medium text-dark/80"
                >
                  <LogIn size={18} />
                  {t("signIn")}
                </Link>
              )}
              <LanguageSwitcher variant="light" />
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
